import { Action, Reducer } from "redux";
import { CompanyIsValidAction, COMPANY_IS_VALID } from ".";
import { AppThunkAction } from "..";
import { GetDateFromDateTime } from "../../helperFunctions";
import { INSURANCE_SELF_RISK_CHANGED, InsuranceStartState, INSURANCE_SELECTED_COVERAGE_CHANGED } from "../Insurance";
import { TextInputState } from "../interfaces";

export const BONUS_CHANGED = "BONUS_CHANGED";
export const BONUS_VALID = "BONUS_VALID";
export const BONUS_INVALID = "BONUS_INVALID";
export const COMPANY_CHANGED = "COMPANY_CHANGED";
export const REMEMBER_REG_NO_CHANGED = "REMEMBER_REG_NO_CHANGED";
export const PREVIOUS_REG_NO_CHANGED = "PREVIOUS_REG_NO_CHANGED";
export const COMPANY_INPUT_CHANGED = "COMPANY_INPUT_CHANGED";

const NissanNo_values = [
  {
    "id": "B75",
    "value": "75%",
    "bonusOriginNeeded": "Mandatory"
  },
  {
    "id": "B70",
    "value": "70%",
    "bonusOriginNeeded": "Mandatory"
  },
  {
    "id": "B60",
    "value": "60%",
    "bonusOriginNeeded": "Mandatory"
  },
  {
    "id": "Startbonus",
    "value": "Startbonus",
    "bonusOriginNeeded": "NotAllowed"
  }
];


export interface AvailableBonus {
  id: string;
  value: string;
  bonusOriginNeeded: string;
}

export interface Bonus {
  help?: { title?: string; description?: string };
  label?: { id?: string; title?: string };
  selectedId?: string;
  bonusOriginNeeded?: boolean;
  availableBonuses?: AvailableBonus[];
  bottomText?: string;
  requiredErrorMessage?: string;
  showError: boolean;
}

export interface BonusState {
  bonus?: Bonus;
  previousCompany: PreviousCompany;
  registrationNumber: RegistrationNumber;
  date: InsuranceStartState;
  isValid: boolean;
}

export interface PreviousCompany {
  company: Company[];
  help?: { title?: string; description?: string };
  label: { id?: string; title?: string };
  selected: string;
  companyInput?: TextInputState;
  requiredErrorMessage?: string;
  showError: boolean;
}

export interface Company {
  value: string;
  title: string;
}

export interface RegistrationNumber {
  title: string;
  selected?: boolean;
  values: RememberPreviousRegNoValue[];
  description?: { title: string; text: string };
  requiredErrorMessage?: string;
  showError: boolean;
  regNumber: TextInputState;
}

export interface RememberPreviousRegNoValue {
  value: boolean;
  text: string;
}

// for dev. purposes
const defaults = {
  bonus: {
    selectedId: "B75",
    bonusOriginNeeded: true,
    help: {
      title: "Hur långt kör du per år?",
      description:
        "Välj det milintervall som motsvarar hur många mil du kör på ett år. Priset på försäkringen påverkas av hur långt du kör.",
    },
    label: {
      id: "2",
      title: "Hva er din nåværende bonus?",
    },
    availableBonuses: NissanNo_values,
    bottomText: "Vi ber deg oppgi selskapet bonusen skal hentes fra før du sluttfører kjøpet.",
    showError: false,
    requiredErrorMessage: "error",
  },
  previousCompany: {
    company: [
      {
        value: "1",
        title: "Some Insurance company",
      },
      {
        value: "2",
        title: "Some Insurance company",
      },
    ],
    help: {
      title: "help",
      description: "help",
    },
    label: {
      title: "Hvilket selskap?",
    },
    selected: "0",
    companyInput: {
      title: "Previous Insurance company",
      state: 0,
      stateDescriptions: ["", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
    showError: false,
    requiredErrorMessage: "error",
  },
  registrationNumber: {
    title: "Vet du registeringsnummer på kjøretøyet bonusen står på?",
    selected: undefined,
    values: [
      {
        value: true,
        text: "Ja",
      },
      {
        value: false,
        text: "Nej",
      },
    ],
    requiredErrorMessage: "error",
    showError: false,
    regNumber: {
      title: "Skriv in registreringsnummer",
      state: 0,
      stateDescriptions: ["Default", "NotSet", "Incorrect"],
      secureValue: "",
      value: "",
    },
  },
  date: {
    value: GetDateFromDateTime(new Date()),
    title: "Forsikringen skal gjelde fra",
    isDisabled: true,
    description: {
      title: "tt2",
      text: "tt2",
    },
  },
  isValid: true,
};

export type ChangeAction = {
  type:
  | typeof BONUS_CHANGED
  | typeof COMPANY_CHANGED
  | typeof REMEMBER_REG_NO_CHANGED
  | typeof PREVIOUS_REG_NO_CHANGED
  | typeof INSURANCE_SELF_RISK_CHANGED
  | typeof INSURANCE_SELECTED_COVERAGE_CHANGED
  | typeof COMPANY_INPUT_CHANGED
  | typeof BONUS_INVALID
  | typeof BONUS_VALID;
  payload: { value: any };
};

export type ValidAction = ChangeAction & CompanyIsValidAction;

const getInputState = (value: string) => (value.length > 0 ? 0 : 1);

const isValid = (state: BonusState): BonusState => {
  const companyState = getInputState(state.previousCompany.companyInput?.value ?? "");
  const isInputValid = companyState === 0;
  const selectedBonus = state?.bonus?.availableBonuses?.find(b => b.id === state?.bonus?.selectedId);
  const bonusOriginNeeded = selectedBonus?.bonusOriginNeeded === "Mandatory";

  if (state.previousCompany.selected === "0" && bonusOriginNeeded) {
    return {
      ...state,
      previousCompany: {
        ...state.previousCompany,
        showError: true,
      },
      isValid: false,
    };
  }

  if (
    state.previousCompany.selected !== "XXXX" ||
    (state.previousCompany.selected === "XXXX" &&
      state.previousCompany.companyInput?.state === companyState &&
      state.isValid === isInputValid)
  ) {
    return {
      ...state,
      isValid: true,
    };
  }

  return {
    ...state,
    previousCompany: {
      ...state.previousCompany,
      showError: false,
      companyInput: {
        ...state.previousCompany.companyInput,
        state: companyState,
        stateDescriptions: state.previousCompany.companyInput?.stateDescriptions ?? [],
      },
    },
    isValid: isInputValid,
  };
};

export const reducer: Reducer<BonusState> = (state: BonusState = defaults, input: Action | CompanyIsValidAction): BonusState => {
  const action: ChangeAction | CompanyIsValidAction = input as ValidAction;

  switch (action?.type) {
    case BONUS_CHANGED:
      const selectedBonus = state.bonus?.availableBonuses?.find(b => b.id === action.payload.value);
      const bonusOriginNeeded = selectedBonus?.bonusOriginNeeded === "Mandatory";
      return {
        ...state,
        bonus: {
          ...state.bonus,
          selectedId: action.payload.value,
          bonusOriginNeeded: bonusOriginNeeded,
          showError: false,
        },
      };
    case COMPANY_CHANGED:
      return {
        ...state,
        previousCompany: {
          ...state.previousCompany,
          selected: action.payload.value,
          showError: false,
        },
      };
    case REMEMBER_REG_NO_CHANGED:
      return {
        ...state,
        registrationNumber: {
          ...state.registrationNumber,
          selected: action.payload.value,
        },
      };
    case PREVIOUS_REG_NO_CHANGED:
      return {
        ...state,
        registrationNumber: {
          ...state.registrationNumber,
          regNumber: {
            ...state.registrationNumber.regNumber,
            value: action.payload.value,
          },
        },
      };
    case COMPANY_INPUT_CHANGED:
      return {
        ...state,
        previousCompany: {
          ...state.previousCompany,
          companyInput: {
            ...state.previousCompany.companyInput,
            value: action.payload.value,
            state: 0,
            stateDescriptions: state.previousCompany.companyInput?.stateDescriptions ?? [],
          },
        },
      };
    case COMPANY_IS_VALID:
      return isValid(state);

    case BONUS_INVALID:
      return {
        ...state,
        bonus: {
          ...state.bonus,
          showError: true,
        },
      };

    case BONUS_VALID:
      return {
        ...state,
        bonus: {
          ...state.bonus,
          showError: false,
        },
      };

    default:
      return state;
  }
};

export const actions = {
  companyChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: COMPANY_CHANGED,
          payload: { value: value as string },
        });
      },
  rememberRegNoQuestionChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: REMEMBER_REG_NO_CHANGED,
          payload: { value: value as boolean },
        });
      },
  previousRegNoChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: PREVIOUS_REG_NO_CHANGED,
          payload: { value: value as string },
        });
      },
  companyInputChanged:
    (value: unknown): AppThunkAction<ValidAction | {}> =>
      dispatch => {
        dispatch({
          type: COMPANY_INPUT_CHANGED,
          payload: { value: value as string },
        });
      },
};
